import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useState, useEffect, useRef, useMemo } from "react";
import { useFirebase } from 'containers/FirebaseContext';
import {  useLocation } from 'react-router-dom';
import { useCurrentClass } from 'store/classes/selectors';
import { useCurrentUser } from 'store/users/selectors';
import { useAllBook } from 'store/books/selectors';
import queryString from 'query-string';
import { doc, setDoc, serverTimestamp, collection, onSnapshot, query, getDoc } from 'firebase/firestore';
import * as Act from './actions';
import { logToSentry } from "utils/networkErrorUtils";
import { find, get } from 'lodash';
import { useAlert } from 'components/UI/alertSakeBar/alertProvider';
import useBookCollection from 'components/hooks/books/useBookCollection';
import useIsExamActive from 'components/hooks/books/useIsExamActive';

const selectore = (state) => {
  return state.class_user_logs
};
export const useClassStudentLogs = () => {
  const selectedData = useSelector(selectore, shallowEqual)
  return selectedData;
}



export function useStudentClassPathLogListner() {
  const { firestore , firebaseUser } = useFirebase();
  const currentClass = useCurrentClass();
  const currentUser = useCurrentUser();
  const [class_user_logs, setClassUserLogs] = useState([])
  const dispatch = useDispatch();
  useEffect(() => {
    if(!firebaseUser || (firebaseUser && Object.keys(firebaseUser).length === 0)) return;
    dispatch(Act.clearActiveClassStudentLog())
    if (!currentClass?.id || !currentUser?.id) return;
    if (currentUser.role === 'student') return;
    const classId = currentClass.id;
    const logsRef = collection(firestore, `class_logs/${classId}/user_logs`);
    const q = query(logsRef);
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const usersList = [];
      querySnapshot.forEach((doc) => {
        usersList.push({ id: doc.id, ...doc.data() });
      });
      setClassUserLogs(usersList)
      dispatch(Act.setActiveClassStudentLog(usersList))
    });
    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [currentUser, currentClass, firestore,firebaseUser]);
  return class_user_logs
}



export function useActionPathLog() {
  
  const location = useLocation();
  const currentUser = useCurrentUser();
  const currentClass = useCurrentClass();
  const { firestore , firebaseUser } = useFirebase();
  const lastPathname = useRef('');
  const intervalId = useRef(null);
  useEffect(() => {
    const logRef = currentUser?.id && currentClass?.id
      ? doc(firestore, `class_logs/${currentClass.id}/user_logs`, currentUser.id)
      : null;

    const updateLog = async (location, resetIdleTime = false) => {
      if (!logRef|| !firebaseUser) return;
      const logSnapshot = await getDoc(logRef);
      let idleTime = logSnapshot.exists() ? logSnapshot.data().idle_time || 0 : 0;
      if (resetIdleTime || location.pathname !== lastPathname.current) {
        idleTime = 0;
        lastPathname.current = location.pathname;
      } else {
        idleTime += 3;
      }
      const searchParams = queryString.parse(location.search);
      const logEntry = {
        userId: currentUser.id,
        classId: currentClass.id,
        lastVisitedPage: location.pathname,
        timestamp: serverTimestamp(),
        query: searchParams,
        active: true,
        idle_time: idleTime
      };

      setDoc(logRef, logEntry, { merge: true })
        .catch(error => {
          logToSentry({
              message: 'Error updating page visit',
              error,
              module: 'LIVE_LOGS',
              operation: 'UPDATE_PAGE_VISIT'
          });
          console.error("Error updating page visit:", error);
        });
    };

    // Clear any existing interval
    if (intervalId.current) {
      clearInterval(intervalId.current);
    }

    // Update log when location changes and user is a student
    if (currentClass?.id && currentUser?.id && currentUser.role === 'student') {
      updateLog(location, true);
      intervalId.current = setInterval(() => updateLog(location), 180000);
    }

    return () => {
      if (intervalId.current) {
        clearInterval(intervalId.current);
      }
    };
  }, [location, currentUser, currentClass, firestore]); // React to location changes

  async function updateFirebaseOnClose() {
    try {
      const last_log = sessionStorage.getItem('last_log');
      const last_log_user = sessionStorage.getItem('last_log_user');
      const last_log_class = sessionStorage.getItem('last_log_class');
      if (last_log && last_log_user && last_log_class) {
        const logRef = doc(firestore, last_log, last_log_user);
        const logEntry = {
          userId: last_log_user,
          classId: last_log_class,
          lastVisitedPage: '/',
          timestamp: serverTimestamp(),
          query: {},
          active: false
        };
        await setDoc(logRef, logEntry, { merge: true })
        sessionStorage.removeItem('last_log');
        sessionStorage.removeItem('last_log_user');
        sessionStorage.removeItem('last_log_class');
      }
    } catch (error) {
      logToSentry({
          message: 'Error during Firebase updateFirebaseOnClose',
          error,
          module: 'LIVE_LOGS',
          operation: 'UPDATE_FIREBASE_ON_CLOSE'
      });
    }
  }
  return { updateFirebaseOnClose };
}

export const useUrlBroadcaster = () => {
  const { firestore , firebaseUser } = useFirebase();
  const currentClass = useCurrentClass();
  const currentUser = useCurrentUser();
  const location = useLocation();
  const showAlert = useAlert();
  const {hiddenBookGroupBookId, hiddenBookId, nonAssignableBookIds} = useBookCollection();
  const {isExamActive, examBookIds } = useIsExamActive();


  const broadcastUrlToClass = async () => {
    if (!currentClass?.id || !firebaseUser) return;
    const searchParams = queryString.parse(location.search);
    const chapter = searchParams.chapter;

    if (chapter) {
      if (nonAssignableBookIds?.includes(Number(chapter))) {
        const error = new Error('Cannot move students to teacher-only content');
        error.type = 'NON_ASSIGNABLE';
        throw error;
      } else if (hiddenBookGroupBookId?.includes(Number(chapter))) {
        const error = new Error('Cannot move students to hidden Book Group');
        error.type = 'HIDDEN_BOOK_GROUP';
        throw error;
      } else if (hiddenBookId?.includes(Number(chapter))) {
        const error = new Error('Cannot move students to hidden content');
        error.type = 'HIDDEN_BOOK';
        throw error;
      } else if (examBookIds?.includes(Number(chapter))) {
        if (!isExamActive(chapter)) {
          const error = new Error('Cannot move students to inactive exam');
          error.type = 'INACTIVE_EXAM';
          throw error;
        }
      }
    }
    
    const classId = currentClass.id;
    const classDocRef = doc(firestore, 'classes_broadcast_url', classId);
    try {
      const path = location.pathname === '/admin/class' ? "/student/class" : location.pathname;
      let search = location.search;
      search = search.replace('view=manage', 'view=home')
        .replace('view=gradebook', 'view=assessments')
        .replace('view=classdata', 'view=studentData');
      
      const targetUrl = `${path}${search}`;
      
      await setDoc(classDocRef, {
        activeUrl: targetUrl,
        actionTimestamp: serverTimestamp(),
        actionBy: currentUser.id,
        classId: classId,
        active: true,
        retryCount: 0,
        lastAttempt: serverTimestamp()
      }, { merge: true });

      setTimeout(async () => {
        try {
          await setDoc(classDocRef, {
            activeUrl: '',
            actionTimestamp: serverTimestamp(),
            active: false,
            retryCount: 0
          }, { merge: true });
        } catch (clearError) {
          console.error('Error clearing URL:', clearError);
          logToSentry({
              message: 'Error clearing broadcast URL',
              error: clearError,
              module: 'LIVE_LOGS',
              operation: 'CLEAR_BROADCAST_URL'
          });
        }
      }, 5000);
      
      showAlert('Students are being moved to this page...', 'success');
    } catch (error) {
      console.error('Error broadcasting URL:', error);
      logToSentry({
          message: 'Error broadcasting URL',
          error,
          module: 'LIVE_LOGS',
          operation: 'BROADCAST_URL'
      });
      showAlert('Failed to move students. Please try again.', 'error');
      
      try {
        await setDoc(classDocRef, {
          activeUrl: '',
          actionTimestamp: serverTimestamp(),
          active: false,
          error: error.message
        }, { merge: true });
      } catch (cleanupError) {
        console.error('Error during cleanup:', cleanupError);
      }
    }
  };

  return broadcastUrlToClass;
};
export const useMessageBroadcaster = () => {
  const { firestore , firebaseUser } = useFirebase();
  const currentClass = useCurrentClass();
  const currentUser = useCurrentUser();
  const broadcastMessageToClass = async (message, users, poster = '') => {
    if (!currentClass?.id || !firebaseUser) return;
    const classId = currentClass.id;
    const classDocRef = doc(firestore, 'classes_broadcast_message', classId);
    try {
      await setDoc(classDocRef, {
        message: message,
        poster: poster,
        actionTimestamp: serverTimestamp(),
        actionBy: currentUser.id,
        classId: classId,
        active: true,
        users: users
      }, { merge: true });

      setTimeout(async () => {
        await setDoc(classDocRef, {
          message: '',
          actionTimestamp: serverTimestamp(),
          active: false,
          users: []
        }, { merge: true });
      }, 5000);
    } catch (error) {
      logToSentry({
          message: 'Error broadcasting message',
          error,
          module: 'LIVE_LOGS',
          operation: 'BROADCAST_MESSAGE'
      });
      console.error('Error broadcasting message:', error);
    }
  };

  return broadcastMessageToClass;
};


export const useActiveClassUrl = () => {
  const [activeUrl, setActiveUrl] = useState('');
  const currentClass = useCurrentClass();
  const currentUser = useCurrentUser();
  const { firestore , firebaseUser } = useFirebase();
  useEffect(() => {
    if (!currentClass?.id || !currentUser?.id || !firebaseUser ) return;
    if (currentUser.role != 'student') return;
    const classDocRef = doc(firestore, 'classes_broadcast_url', currentClass.id);
    const unsubscribe = onSnapshot(classDocRef, (doc) => {
      try {
        if (doc.exists()) {
          const data = doc.data();
          const url = data.activeUrl;
          const actionTime = data.actionTimestamp?.toDate().getTime();
          const retryCount = data.retryCount || 0;
          
          if (url && actionTime && (Date.now() - actionTime) <= 5000) {
            if (retryCount < 3) {
              setActiveUrl(url);
            } else {
              console.warn('URL broadcast exceeded retry limit, ignoring');
              setActiveUrl('');
            }
          } else {
            setActiveUrl('');
          }
        } else {
          setActiveUrl('');
        }
      } catch (error) {
        console.error('Error processing URL broadcast:', error);
        logToSentry({
            message: 'Error in useActiveClassUrl',
            error,
            module: 'LIVE_LOGS',
            operation: 'USE_ACTIVE_CLASS_URL'
        });
        setActiveUrl('');
      }
    }, (error) => {
      console.error('Error listening to URL broadcast:', error);
      logToSentry({
          message: 'Error in useActiveClassUrl listener',
          error,
          module: 'LIVE_LOGS',
          operation: 'USE_ACTIVE_CLASS_URL_LISTENER'
      });
    });

    return () => unsubscribe();
  }, [currentClass, currentUser, firebaseUser , firestore]);

  return activeUrl;
};
export const useActiveClassBrodcastMessage = () => {
  const [active_message, setActiveMessage] = useState('');
  const currentClass = useCurrentClass();
  const currentUser = useCurrentUser();
  const { firestore , firebaseUser } = useFirebase();
  const { invokeMessageAlert, revokeMessageAlert } = useMessageAlertView()
  useEffect(() => {
    if (!currentClass?.id || !currentUser?.id || !firebaseUser) return;
    if (currentUser.role != 'student') return;
    let timoutref = null;
    const classDocRef = doc(firestore, 'classes_broadcast_message', currentClass.id);
    const unsubscribe = onSnapshot(classDocRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const message = data.message;
        const poster = data.poster || '';
        const users = data.users || [];
        const actionTime = data.actionTimestamp?.toDate().getTime();
        if (data.active && message && actionTime && (Date.now() - actionTime) <= 5000) {
          if (users.length == 0 || users.includes(currentUser.id)) {
            if (timoutref) {
              clearTimeout(timoutref)
            }
            setActiveMessage(message);
            invokeMessageAlert({ message, poster });
            timoutref = setTimeout(() => {
              revokeMessageAlert()
              setActiveMessage('');
              timoutref = null;
            }, 5000)
          }
        } else {
          setActiveMessage('');
        }
      }
    });

    return () => unsubscribe();
  }, [currentClass, currentUser, firebaseUser]);

  return active_message;
};


export const useIsUriMovable = () => {
  const location = useLocation();
  const booksAllList = useAllBook();
  const currentClass = useCurrentClass();
  const currentUser = useCurrentUser();

  return useMemo(() => {
    const movablePaths = [
      "/ebook", "/1nformate", "/informate", "/informateBook",
      "/resources", "/video", "/OneWayLessons", "/balconCultural",
      "/balconCulturalBook", "/Conectandome", "/ConectandomeBook",
      "/comunidad", "/ComunidadBook", "/exams", "/story-levels",
      "/stories", "/graphic_organizers", "/ap", "/apBook",
      "/admin/class"
    ];

    if (!movablePaths.includes(location.pathname)) {
      return false;
    }
    const searchParams = queryString.parse(location.search);
    const chapter = searchParams.chapter;
    
    if (chapter) {
      const book = find(booksAllList, { id: chapter });
      if (book?.onlyTeacher && currentUser.role === 'student') {
        return false;
      }
      const getHiddenBooks = get(currentClass, "hide_books", []).map(data => data?.bookId) || [];
      if (currentUser.role === 'student' && getHiddenBooks.includes(chapter)) {
        return false;
      }
    }

    return true;
  }, [location.pathname, location.search, booksAllList, currentClass, currentUser]);
};

const messageAlertSelector = state => state.messageAlertView
export const useMessageAlertView = () => {
  const messageAlertView = useSelector(messageAlertSelector, shallowEqual)
  const dispatch = useDispatch()
  const invokeMessageAlert = (payload) => {
    dispatch(Act.setMessageAlertView(payload))
  }
  const revokeMessageAlert = () => {
    dispatch(Act.revokeMessageAlerView())
  }
  return { messageAlertView, invokeMessageAlert, revokeMessageAlert };
}
